import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tokenService } from '../../../services/tokenService';
import { apiSlice } from '../../../store/api/apiSlice';
import { setSession } from '../../../store/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { clearStytchCookies } from '../providers/clearStytchCookies';

const AuthDevPanel: React.FC = () => {
  const isDev = import.meta.env.DEV;
  const dispatch = useAppDispatch();
  const auth = useAppSelector((s) => s.auth);

  const loginAsMember = useCallback(async () => {
    void clearStytchCookies();
    tokenService.clearTokens();
    await tokenService.devLogin('member@localhost');
  }, []);

  const simulateTimeout = useCallback(() => {
    void clearStytchCookies();
    tokenService.clearTokens({
      expired: true,
      message: 'Session expired due to simulated idle timeout.',
    });
  }, []);

  const simulateUnauthorized = useCallback(async () => {
    void clearStytchCookies();
    let platformUser: any = null;
    try {
      const raw = sessionStorage.getItem('platform_user');
      if (raw) platformUser = JSON.parse(raw);
    } catch {
      platformUser = null;
    }

    dispatch(
      setSession({
        token: 'bad-token',
        user: platformUser ?? undefined,
        message: 'Forced unauthorized request',
      })
    );

    try {
      await dispatch(apiSlice.endpoints.getMe.initiate(null, { forceRefetch: true })).unwrap();
    } catch {
      // Expected 401; noop
    }
  }, [dispatch]);

  const resetAuth = useCallback(() => {
    void clearStytchCookies();
    tokenService.clearTokens();
  }, []);

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (!isPopoverVisible) {
      hoverTimeoutRef.current = window.setTimeout(() => {
        setIsPopoverVisible(true);
        hoverTimeoutRef.current = null;
      }, 3000);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    hideTimeoutRef.current = window.setTimeout(() => {
      setIsPopoverVisible(false);
      hideTimeoutRef.current = null;
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  if (!isDev) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        p: 2,
        pointerEvents: 'none',
        width: 320,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          pointerEvents: 'auto',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Box
          className="auth-dev-panel-handle"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 120,
            height: 32,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: 10,
            letterSpacing: 1,
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 150ms ease',
          }}
        >
          Auth dev
        </Box>
        <Paper
          elevation={3}
          className="auth-dev-panel-popover"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            p: 2,
            opacity: isPopoverVisible ? 1 : 0,
            pointerEvents: isPopoverVisible ? 'auto' : 'none',
            transform: isPopoverVisible ? 'translate(-50%, -8px)' : 'translate(-50%, 12px)',
            transition: 'opacity 180ms ease, transform 180ms ease',
            maxWidth: 280,
            position: 'absolute',
            left: '50%',
            bottom: 48,
            transformOrigin: 'center bottom',
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Auth Dev Tools</Typography>
              <Chip
                size="small"
                label={auth.status}
                color={
                  auth.status === 'authenticated'
                    ? 'success'
                    : auth.status === 'expired'
                      ? 'warning'
                      : 'default'
                }
              />
            </Stack>
            {auth.sessionMessage ? (
              <Typography variant="caption" color="warning.main">
                {auth.sessionMessage}
              </Typography>
            ) : null}
            <Stack spacing={1}>
              <Button variant="contained" size="small" color="primary" onClick={loginAsMember}>
                Login as member
              </Button>
              <Button variant="outlined" size="small" onClick={simulateTimeout}>
                Simulate idle timeout
              </Button>
              <Button variant="outlined" size="small" onClick={simulateUnauthorized}>
                Simulate 401 on next call
              </Button>
              <Button variant="text" size="small" onClick={resetAuth}>
                Reset auth state
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default AuthDevPanel;
